import * as SelectPrimitive from '@radix-ui/react-select';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SearchIcon,
} from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Typing filters the open list. The content owns the query; each item reports
 * whether it matches so the content can tell when nothing does.
 */
interface SelectFilter {
  query: string;
  /** `null` when the item unmounts. */
  report: (id: string, matches: boolean | null) => void;
}

const SelectFilterContext = React.createContext<SelectFilter | null>(null);

/** Case- and accent-insensitive, so "espanol" finds "Español". */
function normalise(text: string) {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}

/**
 * Lets a keystroke on the closed trigger open the list and start the filter
 * with that character, instead of Radix silently jumping the value to the
 * first option that starts with it.
 */
interface SelectRoot {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Typed on the trigger; the content takes it as the opening query. */
  seed: React.RefObject<string>;
}

const SelectRootContext = React.createContext<SelectRoot | null>(null);

function Select({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const [openState, setOpenState] = React.useState(defaultOpen ?? false);
  const open = openProp ?? openState;
  const seed = React.useRef('');

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange]
  );

  const root = React.useMemo(
    () => ({ open, setOpen, seed }),
    [open, setOpen]
  );

  return (
    <SelectRootContext.Provider value={root}>
      <SelectPrimitive.Root
        data-slot="select"
        open={open}
        onOpenChange={setOpen}
        {...props}
      />
    </SelectRootContext.Provider>
  );
}

function SelectGroup(
  props: React.ComponentProps<typeof SelectPrimitive.Group>
) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue(
  props: React.ComponentProps<typeof SelectPrimitive.Value>
) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  children,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  const root = React.useContext(SelectRootContext);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || !root || props.disabled) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    // Space still opens the list, as Radix handles it.
    if (event.key.length !== 1 || event.key === ' ') return;
    event.preventDefault();
    root.seed.current = event.key;
    root.setOpen(true);
  };

  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      onKeyDown={handleKeyDown}
      className={cn(
        'flex h-9 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none',
        'data-[placeholder]:text-muted-foreground',
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
        '*:data-[slot=select-value]:truncate',
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 shrink-0 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  onKeyDown,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  const [matches, setMatches] = React.useState<ReadonlyMap<string, boolean>>(
    () => new Map()
  );
  const inputRef = React.useRef<HTMLInputElement>(null);
  const viewportRef = React.useRef<HTMLDivElement>(null);
  // Set when a keystroke on an option, or on the closed trigger, is
  // redirected into the search box.
  const focusSearch = React.useRef(false);

  const report = React.useCallback((id: string, match: boolean | null) => {
    setMatches((previous) => {
      if (match === null ? !previous.has(id) : previous.get(id) === match) {
        return previous;
      }
      const next = new Map(previous);
      if (match === null) next.delete(id);
      else next.set(id, match);
      return next;
    });
  }, []);

  const filter = React.useMemo(() => ({ query, report }), [query, report]);

  const root = React.useContext(SelectRootContext);
  const open = root?.open ?? false;
  React.useLayoutEffect(() => {
    if (!open || !root?.seed.current) return;
    focusSearch.current = true;
    setQuery(root.seed.current);
    root.seed.current = '';
  }, [open, root]);

  React.useLayoutEffect(() => {
    if (!focusSearch.current) return;
    focusSearch.current = false;
    inputRef.current?.focus();
  }, [query]);

  let matchCount = 0;
  for (const match of matches.values()) if (match) matchCount++;

  /**
   * Radix keeps focus on the options, and its own typeahead only jumps to the
   * next option starting with the typed letter. Take the keystroke first —
   * preventing default skips Radix's handler — and feed it to the filter.
   */
  const handleContentKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.target === inputRef.current) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    let next: string;
    if (event.key === 'Backspace') {
      if (query === '') return;
      next = query.slice(0, -1);
    } else if (event.key.length === 1) {
      // Space on an option selects it; only treat it as text mid-query.
      if (event.key === ' ' && query === '') return;
      next = query + event.key;
    } else {
      return;
    }

    event.preventDefault();
    focusSearch.current = true;
    setQuery(next);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // Pick the first match through Radix's own keyboard path, so selecting
      // and closing behave exactly as if the option had been focused.
      const first = viewportRef.current?.querySelector<HTMLElement>(
        '[role="option"]:not([data-disabled])'
      );
      if (!first) return;
      first.focus();
      first.dispatchEvent(
        new KeyboardEvent('keydown', { key: 'Enter', bubbles: true })
      );
      return;
    }
    // Arrows move into the list and Tab/Escape keep their meaning; every
    // other key belongs to the text field, not Radix's typeahead.
    if (!['ArrowDown', 'ArrowUp', 'Tab', 'Escape'].includes(event.key)) {
      event.stopPropagation();
    }
  };

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        className={cn(
          'bg-popover text-popover-foreground relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] overflow-hidden rounded-lg border shadow-lg',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
          className
        )}
        onKeyDown={handleContentKeyDown}
        onCloseAutoFocus={(event) => {
          onCloseAutoFocus?.(event);
          setQuery('');
        }}
        {...props}
      >
        <SelectFilterContext.Provider value={filter}>
          {/*
            Always shown, even over three options: it is the only sign that
            typing filters. It must also never unmount while focused — focus
            then falls to the list, and the page's scroll container jumps.
          */}
          <div className="flex shrink-0 items-center gap-2 border-b px-3">
            <SearchIcon className="size-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={t('common.typeToFilter')}
              aria-label={t('common.typeToFilter')}
              autoComplete="off"
              spellCheck={false}
              className="placeholder:text-muted-foreground h-9 w-full min-w-0 bg-transparent text-base outline-none md:text-sm"
            />
          </div>
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            ref={viewportRef}
            className={cn(
              'p-1',
              position === 'popper' &&
                'h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width) scroll-my-1'
            )}
          >
            {children}
            {query !== '' && matchCount === 0 && (
              <p className="text-muted-foreground px-2 py-4 text-center text-sm">
                {t('common.noOptionsMatch')}
              </p>
            )}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectFilterContext.Provider>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  const filtering = !!React.useContext(SelectFilterContext)?.query;
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      hidden={filtering || undefined}
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  textValue,
  disabled,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  const filter = React.useContext(SelectFilterContext);
  const query = filter?.query ?? '';
  const report = filter?.report;
  const id = React.useId();
  const ref = React.useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = React.useState(false);

  // Match on the rendered text, so labels built from components (translated
  // names, badges) filter the same as plain strings.
  React.useLayoutEffect(() => {
    const needle = normalise(query);
    const haystack = normalise(textValue ?? ref.current?.textContent ?? '');
    const match = needle === '' || haystack.includes(needle);
    setHidden(!match);
    report?.(id, match);
  }, [query, textValue, children, report, id]);

  React.useLayoutEffect(() => () => report?.(id, null), [report, id]);

  return (
    <SelectPrimitive.Item
      ref={ref}
      // Hidden options are also disabled so arrow keys skip them. Hiding
      // rather than unmounting keeps the selected label in the trigger.
      disabled={disabled || hidden}
      hidden={hidden || undefined}
      textValue={textValue}
      data-slot="select-item"
      className={cn(
        'relative flex w-full cursor-default items-center gap-2 rounded-md py-1.5 pr-8 pl-2 text-sm outline-hidden select-none',
        'focus:bg-accent focus:text-accent-foreground',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  const filtering = !!React.useContext(SelectFilterContext)?.query;
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      hidden={filtering || undefined}
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
